import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingSubmitButton } from "../form_fields/LoadingSubmitButton";
import { iCollection, patchCollection } from "@/services/collection";
import { iGroup } from "@/services/groups";
import { LargeText } from "../typography/Typography";
import { Input } from "../ui/input";
import { SelectSeparator } from "@/components/ui/select";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import axios from "axios";
import { ExternalLinkIcon } from "lucide-react";

const formSchema = z.object({
  bookingComId: z.string().optional(),
  redirect: z
    .object({
      exampleUrl: z.string().url().optional(),
      description: z.string().optional(),
      useRedirect: z.boolean().optional(),
    })
    .optional(),
  isLoyaltyEnabledAdmin: z.boolean().optional(),
  deltaPriceBookingCom: z.number().optional(),
  deltaPriceHotelsCom: z.number().optional(),
  advantagesOnDirectWebsite: z.string().optional(),
});

export default function FormSettingsTrialPeriod({
  collection,
  onRefresh,
}: {
  collection: iCollection;
  group: iGroup;
  onRefresh(): void;
}) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      bookingComId: collection.preData?.bookingComId || "",
      redirect: collection.preData?.redirect,
      isLoyaltyEnabledAdmin: collection.isLoyaltyEnabledAdmin || false,
      deltaPriceBookingCom:
        collection.preData?.deltaPriceBookingCom || undefined,
      deltaPriceHotelsCom: collection.preData?.deltaPriceHotelsCom || undefined,
      advantagesOnDirectWebsite: collection.preData?.advantagesOnDirectWebsite
        ? collection.preData?.advantagesOnDirectWebsite.join(",")
        : undefined,
    },
  });

  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);

    try {
      await patchCollection({
        collectionId: collection._id,
        isLoyaltyEnabledAdmin: data.isLoyaltyEnabledAdmin,
        preData: {
          bookingComId: data.bookingComId,
          redirect: data.redirect,
          deltaPriceBookingCom: data.deltaPriceBookingCom,
          deltaPriceHotelsCom: data.deltaPriceHotelsCom,
          advantagesOnDirectWebsite: data.advantagesOnDirectWebsite
            ? data.advantagesOnDirectWebsite.split(",")
            : [],
        },
      });

      onRefresh();
    } catch (error) {
      console.error(error);
    }

    setIsSubmitting(false);
  };

  const redirectSettings = useWatch({
    name: "redirect",
    control: form.control,
  });
  const [redirectTest, setRedirectTest] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const generateTestUrl = useCallback(async () => {
    if (
      !redirectSettings?.description ||
      !redirectSettings.exampleUrl ||
      !!redirectTest
    )
      return undefined;

    const today = new Date()

    // Create a copy of the date and add one month
    const nextMonth = new Date(today)
    nextMonth.setMonth(today.getMonth() + 1)
    const dayAfterNextMonth = new Date(nextMonth)
    dayAfterNextMonth.setDate(nextMonth.getDate() + 1)

    try {
      const res = await axios.post("/metasearch/checkout", {
        searchParameters: {
          arrival_date:  nextMonth.toISOString().split('T')[0],
          departure_date: dayAfterNextMonth.toISOString().split('T')[0],
          adults: "2",
          children_age: "1",
        },
        collectionId: collection._id,
        redirectParams: {
          description: redirectSettings.description,
          exampleUrl: redirectSettings.exampleUrl,
          fallbackUrl:
            collection.website ||
            `https://www.google.com/search?${new URLSearchParams({
              q: collection.name,
            }).toString()}`,
        },
      });

      if (res.data) setRedirectTest(res.data);
    } catch (e) {
      console.error("Error generating checkout url", e);
    }

    setIsGenerating(false);
  }, [redirectSettings, collection]);

  return (
    <Form {...form}>
      <form
        id="FormSettingsAnalysisMask"
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div>
          <LargeText>{t("analytics.settings.title")}</LargeText>
          <p className="text-sm text-muted-foreground">
            {t("analytics.settings.description")}
          </p>
        </div>
        <div className="space-y-3">
          <FormField
            control={form.control}
            name="bookingComId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("analytics.settings.bookingComId")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t(
                      "analytics.settings.bookingComId_placeholder"
                    )}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t("analytics.settings.bookingComId_description")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="deltaPriceBookingCom"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("analytics.settings.deltaPriceBookingCom")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step={0.01}
                    placeholder={t(
                      "analytics.settings.deltaPriceBookingCom_placeholder"
                    )}
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormDescription>
                  {t("analytics.settings.deltaPriceBookingCom_description")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="deltaPriceHotelsCom"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("analytics.settings.deltaPriceHotelsCom")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step={0.01}
                    placeholder={t(
                      "analytics.settings.deltaPriceHotelsCom_placeholder"
                    )}
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormDescription>
                  {t("analytics.settings.deltaPriceHotelsCom_description")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="advantagesOnDirectWebsite"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("analytics.settings.advantagesOnDirectWebsite")}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t(
                      "analytics.settings.advantagesOnDirectWebsite"
                    )}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t(
                    "analytics.settings.advantagesOnDirectWebsite_description"
                  )}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <SelectSeparator />

          <div>
            <LargeText>
              {t("analytics.settings.bookingEngineRedirect_title")}
            </LargeText>
            <p className="text-sm text-muted-foreground">
              {t("analytics.settings.bookingEngineRedirect_description")}
            </p>
          </div>

          <FormField
            control={form.control}
            name="redirect.exampleUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("analytics.settings.redirectExampleUrl")}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="https://mybookingengine.com/search?hotel=123&arrival=2025-01-10&departure=2025-01-14&adults=2&childAge=7"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t("analytics.settings.redirectExampleUrl_description")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="redirect.description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("analytics.settings.redirectDescription")}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t(
                      "analytics.settings.redirectDescription_placeholder"
                    )}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t("analytics.settings.redirectDescription_description")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex w-min gap-3">
            <LoadingSubmitButton
              onClick={generateTestUrl}
              isLoading={isGenerating}
              type="button"
              variant="black"
              disabled={
                !redirectSettings?.description ||
                !redirectSettings?.exampleUrl ||
                isGenerating
              }
            >
              {t("analytics.settings.generateTestRedirect_cta")}
            </LoadingSubmitButton>

            {!!redirectTest && (
              <a href={redirectTest} target="_blank">
                <Button
                  variant="secondary"
                  type="button"
                  className="flex gap-2 items-end"
                >
                  {t("analytics.settings.redirectTest_cta")}{" "}
                  <ExternalLinkIcon width={18} />
                </Button>
              </a>
            )}
          </div>

          <FormField
            control={form.control}
            name="redirect.useRedirect"
            render={({ field }) => (
              <FormItem>
                <div className="flex gap-2 items-end">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormLabel>
                    {t("analytics.settings.redirectUseRedirect_description")}
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="isLoyaltyEnabledAdmin"
            render={({ field }) => (
              <FormItem>
                <div className="flex gap-2 items-end">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormLabel>
                    {t("analytics.settings.loyaltyEnabledAdmin_description")}
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-row gap-4">
          <LoadingSubmitButton
            className="ml-auto"
            variant="takyon"
            type="submit"
            isLoading={isSubmitting}
          >
            {t("buttons.save")}
          </LoadingSubmitButton>
        </div>
      </form>
    </Form>
  );
}
